import { useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/cn';
import type { Message } from '@/hooks/useVoiceSession';
import type { LiveMessage } from '@/hooks/useGeminiLive';
import { ConfidenceBadge } from './ConfidenceBadge';
import { DocumentCard } from './DocumentCard';
import { CheckCircle2, ThumbsUp, ThumbsDown } from 'lucide-react';

/**
 * Simple markdown renderer for voice transcripts
 * Handles: **bold**, *italic*, bullet points, numbered lists
 */
function renderMarkdown(text: string): string {
  return text
    // Remove excessive asterisks (e.g., *** or **** patterns)
    .replace(/\*{3,}/g, '')
    // Convert **text** to bold
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    // Convert *text* or _text_ to italic (but not list items)
    .replace(/(?<!\n)\*([^*\n]+)\*(?!\*)/g, '<em>$1</em>')
    .replace(/_([^_]+)_/g, '<em>$1</em>')
    // Convert bullet points (- item or * item at start of line)
    .replace(/^[\-\*]\s+(.+)$/gm, '<li class="ml-4">• $1</li>')
    // Convert numbered lists
    .replace(/^\d+\.\s+(.+)$/gm, '<li class="ml-4">$1</li>')
    // Remove stray asterisks and dots
    .replace(/^\s*[\*•]\s*$/gm, '')
    // Clean up extra whitespace
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// Union type to support both Message and LiveMessage
type TranscriptMessage = Message | LiveMessage;

interface TranscriptProps {
  messages: TranscriptMessage[];
  className?: string;
  maxHeight?: string;
  showTimestamps?: boolean;
  showConfidence?: boolean;
  onAcknowledge?: (messageId: string) => void;
  onFeedback?: (messageId: string, feedback: 'positive' | 'negative') => void;
}

/**
 * Transcript - Real-time message display
 * 
 * Features:
 * - Auto-scroll to latest message
 * - Different styling for user/assistant
 * - Animated message appearance
 * - Partial message support (streaming)
 */
export function Transcript({
  messages,
  className,
  maxHeight = '400px',
  showTimestamps = false,
  showConfidence = true,
  onAcknowledge,
  onFeedback,
}: TranscriptProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  if (messages.length === 0) {
    return (
      <div className={cn(
        'flex items-center justify-center',
        'text-white/40 text-sm',
        'min-h-[100px]',
        className,
      )}>
        <p>Start speaking to begin the conversation...</p>
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      className={cn(
        'overflow-y-auto custom-scrollbar',
        'space-y-4 p-4',
        className,
      )}
      style={{ maxHeight }}
    >
      <AnimatePresence mode="popLayout">
        {messages.map((message) => (
          <MessageBubble
            key={message.id}
            message={message}
            showTimestamp={showTimestamps}
            showConfidence={showConfidence}
            onAcknowledge={onAcknowledge}
            onFeedback={onFeedback}
          />
        ))}
      </AnimatePresence>
    </div>
  );
}

interface MessageBubbleProps {
  message: TranscriptMessage;
  showTimestamp?: boolean;
  showConfidence?: boolean;
  onAcknowledge?: (messageId: string) => void;
  onFeedback?: (messageId: string, feedback: 'positive' | 'negative') => void;
}

function MessageBubble({ message, showTimestamp, showConfidence, onAcknowledge, onFeedback }: MessageBubbleProps) {
  const isUser = message.role === 'user';

  // Get message content (handle both Message and LiveMessage)
  const content = 'content' in message ? message.content : message.text;
  const isFinal = 'isFinal' in message ? message.isFinal : true;
  // Check streaming state for real-time display (BUG-001 fix)
  const isStreaming = 'isStreaming' in message ? message.isStreaming : false;

  // Get confidence data if available (LiveMessage only)
  const confidence = 'confidence' in message ? message.confidence : undefined;
  const confidencePercentage = 'confidencePercentage' in message ? message.confidencePercentage : undefined;
  const confidenceReason = 'confidenceReason' in message ? message.confidenceReason : undefined;
  const requiresReview = 'requiresReview' in message ? message.requiresReview : false;
  const reviewedAt = 'reviewedAt' in message ? message.reviewedAt : undefined;
  // Get feedback from message (BUG-002 fix)
  const feedback = 'feedback' in message ? message.feedback : undefined;

  // Get generated image data (BUG-016)
  const imageBase64 = 'imageBase64' in message ? message.imageBase64 : undefined;
  const imageMimeType = 'imageMimeType' in message ? message.imageMimeType : 'image/jpeg';

  // Get generated document data (EPIC-17)
  const documentId = 'documentId' in message ? message.documentId : undefined;
  const documentTitle = 'documentTitle' in message ? message.documentTitle : undefined;
  const documentType = 'documentType' in message ? message.documentType : undefined;
  const documentFilename = 'documentFilename' in message ? message.documentFilename : undefined;
  const documentMimeType = 'documentMimeType' in message ? message.documentMimeType : undefined;

  // Determine if this message needs acknowledgment (BUG-002: allow feedback on any model message)
  const canProvideFeedback = !isUser && !isStreaming && !feedback && !imageBase64 && !documentId;
  const needsAcknowledgment = !isUser && requiresReview && !reviewedAt && !feedback;
  const isAcknowledged = !isUser && (reviewedAt || feedback);

  // Render markdown for assistant messages, plain text for user
  const renderedContent = useMemo(() => {
    if (isUser) return content;
    return renderMarkdown(content);
  }, [content, isUser]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      className={cn(
        'flex',
        isUser ? 'justify-end' : 'justify-start',
      )}
    >
      <div
        className={cn(
          'max-w-[80%] rounded-2xl px-4 py-3',
          'backdrop-blur-md',
          isUser
            ? 'bg-gradient-to-br from-blue-500/30 to-indigo-500/30 border border-blue-400/30'
            : 'bg-gradient-to-br from-white/10 to-white/5 border border-white/10',
          isStreaming === true && 'animate-pulse',
          // Highlight messages needing acknowledgment
          needsAcknowledgment && 'ring-1 ring-amber-500/50',
        )}
      >
        {/* Role label and confidence badge */}
        <div className={cn(
          'flex items-center justify-between gap-2 mb-1',
        )}>
          <div className={cn(
            'text-xs font-medium',
            isUser ? 'text-blue-300' : 'text-purple-300',
          )}>
            {isUser ? 'You' : 'DryVox'}
          </div>

          {/* Confidence badge for assistant messages */}
          {showConfidence && !isUser && confidence && (
            <ConfidenceBadge
              level={confidence}
              percentage={confidencePercentage}
              reason={confidenceReason}
              showPercentage={true}
              size="sm"
            />
          )}
        </div>

        {/* Message content - use dangerouslySetInnerHTML for markdown */}
        <div
          className="text-white/90 text-sm leading-relaxed whitespace-pre-wrap prose prose-invert prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: renderedContent }}
        />
        {isStreaming === true && (
          <span className="inline-block w-2 h-4 ml-1 bg-white/60 animate-pulse" />
        )}

        {/* Generated Image Display (BUG-016) */}
        {imageBase64 && (
          <div className="mt-3">
            <img
              src={`data:${imageMimeType};base64,${imageBase64}`}
              alt="Generated image"
              className="max-w-full rounded-lg border border-white/10 shadow-lg"
              style={{ maxHeight: '300px' }}
            />
            <p className="text-xs text-white/40 mt-1 text-center">
              Generated image • Saved to Images tab
            </p>
          </div>
        )}

        {/* Generated Document (EPIC-17) */}
        {documentId && documentType && (
          <div className="mt-3">
            <DocumentCard
              id={documentId}
              title={documentTitle || 'Document'}
              type={documentType as 'pdf' | 'pptx' | 'html' | 'docx'}
              filename={documentFilename || ''}
              showDelete={false}
            />
            <p className="text-xs text-white/40 mt-1">
              Generated document • Also in Downloads tab
            </p>
          </div>
        )}

        {/* Feedback section - BUG-002: Thumbs up/down buttons */}
        {canProvideFeedback && onFeedback && (
          <div className="mt-3 pt-2 border-t border-white/10">
            <div className="flex items-center gap-2">
              <span className="text-xs text-white/50 mr-1">Was this helpful?</span>
              <button
                onClick={() => onFeedback(message.id, 'positive')}
                className={cn(
                  'flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium',
                  'bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300',
                  'border border-emerald-500/30 hover:border-emerald-500/50',
                  'transition-colors'
                )}
                title="Thumbs up - Good response"
              >
                <ThumbsUp className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => onFeedback(message.id, 'negative')}
                className={cn(
                  'flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium',
                  'bg-red-500/20 hover:bg-red-500/30 text-red-300',
                  'border border-red-500/30 hover:border-red-500/50',
                  'transition-colors'
                )}
                title="Thumbs down - Needs improvement"
              >
                <ThumbsDown className="w-3.5 h-3.5" />
              </button>
              {needsAcknowledgment && onAcknowledge && (
                <button
                  onClick={() => onAcknowledge(message.id)}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium',
                    'bg-amber-500/20 hover:bg-amber-500/30 text-amber-300',
                    'border border-amber-500/30 hover:border-amber-500/50',
                    'transition-colors ml-auto'
                  )}
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Acknowledge
                </button>
              )}
            </div>
          </div>
        )}

        {/* Feedback indicator */}
        {isAcknowledged && feedback && (
          <div className="mt-2 flex items-center gap-1.5 text-xs">
            {feedback === 'positive' ? (
              <>
                <ThumbsUp className="w-3 h-3 text-emerald-400" />
                <span className="text-emerald-400/70">Helpful</span>
              </>
            ) : (
              <>
                <ThumbsDown className="w-3 h-3 text-red-400" />
                <span className="text-red-400/70">Needs improvement</span>
              </>
            )}
          </div>
        )}

        {/* Acknowledged without feedback */}
        {isAcknowledged && !feedback && (
          <div className="mt-2 flex items-center gap-1.5 text-xs text-emerald-400/70">
            <CheckCircle2 className="w-3 h-3" />
            Reviewed
          </div>
        )}

        {/* Timestamp */}
        {showTimestamp && (
          <div className="text-xs text-white/40 mt-2">
            {message.timestamp.toLocaleTimeString()}
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default Transcript;

